/**
 * Auth store — Zustand with localStorage persistence + boot hydration.
 *
 * The store mounts `setOn401` so any API 401 anywhere triggers a clean
 * logout (clear all caches + storage + reset stores).
 *
 * A3 (2026-06-27) — Gated routing + 403 interceptors.
 *   Every token transition (hydrate / signIn / signInWithToken) runs
 *   through `routePostAuth(user, flags)` which enforces:
 *     1. password_is_temporary | must_change_password → /customer/change-password
 *     2. role=customer AND !profile_completed | needs_profile_completion
 *                                             → /customer/complete-profile
 *     3. else landingForRole(role)  → role-landing URL
 *   A 403 anywhere carrying `profile_completion_required` or
 *   `password_change_required` triggers the same redirects via the
 *   api-client interceptor hooks. Loyalty / staff pages stay untouched.
 */
import { create } from 'zustand';
import {
  api,
  setOn401,
  setOnProfileCompletionRequired,
  setOnPasswordChangeRequired,
} from './api';
import { IS_CUSTOMER_HOST } from './hostnameGuard';
import { landingForRole } from '@/lib/rbac';

const TOKEN_KEY = 'onestamp.access_token';

let _queryClient = null;
export function bindQueryClient(qc) { _queryClient = qc; }

// Router navigator — bound once from the app root (App.js RootRedirect)
// so the auth-store can issue redirects from outside React trees (the
// api-client 401/403 hooks fire from fetch callbacks, not components).
let _navigate = null;
export function bindNavigator(fn) { _navigate = fn; }

function _goto(path) {
  if (_navigate) {
    try { _navigate(path, { replace: true }); return; } catch { /* fall through */ }
  }
  if (typeof window !== 'undefined') {
    window.location.assign(path);
  }
}

/**
 * Gated routing — the single source of truth for post-auth landing.
 * Called from `hydrate`, `signIn`, `signInWithToken`, and the two 403
 * interceptors. Honours the operator-locked priority order.
 */
export function routePostAuth(user, flags = {}) {
  if (!user) {
    _goto(IS_CUSTOMER_HOST ? '/customer/login' : '/login');
    return;
  }
  const mustChange =
    Boolean(flags.must_change_password) || Boolean(user.password_is_temporary);
  if (mustChange) {
    _goto('/customer/change-password');
    return;
  }
  if (user.role === 'customer') {
    const needsProfile =
      Boolean(flags.needs_profile_completion) || !user.profile_completed;
    if (needsProfile) {
      _goto('/customer/complete-profile');
      return;
    }
  }
  _goto(landingForRole(user.role) || '/');
}

export const useAuthStore = create((set, get) => ({
  token: null,
  user: null,
  loading: true,

  hydrate: async () => {
    const saved = (typeof localStorage !== 'undefined')
      ? localStorage.getItem(TOKEN_KEY)
      : null;
    if (!saved) {
      set({ loading: false });
      return;
    }
    try {
      const me = await api.me(saved);
      set({ token: saved, user: me, loading: false });
      // A3 — re-apply gated routing on cold-boot so a customer who
      // closed the tab mid-Complete-Profile yesterday resumes at the
      // right step today. We ONLY route when the current URL is the
      // customer login/signup/root; otherwise we respect the user's
      // deep-link (e.g. they bookmarked /customer/rewards).
      if (typeof window !== 'undefined' && me?.role === 'customer') {
        const path = window.location.pathname;
        const onAuthEntry =
          path === '/' ||
          path.startsWith('/customer/login') ||
          path.startsWith('/customer/signup');
        const mustGate =
          Boolean(me.password_is_temporary) || !me.profile_completed;
        if (onAuthEntry || mustGate) {
          routePostAuth(me);
        }
      }
    } catch {
      try { localStorage.removeItem(TOKEN_KEY); } catch {}
      set({ token: null, user: null, loading: false });
    }
  },

  /** Legacy email-only signIn — kept for staff pages. */
  signIn: async (email, password) => {
    const res = await api.login(email.trim().toLowerCase(), password);
    try { localStorage.setItem(TOKEN_KEY, res.access_token); } catch {}
    set({ token: res.access_token, user: res.user });
    return res.user;
  },

  /** A3 (2026-06-27) — identifier-based signIn (email OR phone). Runs
   *  the gated router on success so Complete Profile / Change Password
   *  gates are honoured. */
  signInWithIdentifier: async (identifier, password) => {
    const id = identifier.trim();
    const ident = id.includes('@') ? id.toLowerCase() : id;
    const res = await api.loginIdentifier(ident, password);
    try { localStorage.setItem(TOKEN_KEY, res.access_token); } catch {}
    set({ token: res.access_token, user: res.user });
    routePostAuth(res.user, {
      needs_profile_completion: res.needs_profile_completion,
      must_change_password:     res.must_change_password,
    });
    return res.user;
  },

  // Persist + activate a session from an already-minted TokenResponse
  // (e.g. the response of /api/auth/register or a Google/Apple exchange).
  // Accepts either the legacy 2-arg form (accessToken, user) or the new
  // 1-arg form (full TokenResponse). The 1-arg form runs the gated
  // router; the 2-arg form does not (preserves pre-A3 behaviour).
  signInWithToken: (accessTokenOrResponse, user) => {
    // Legacy 2-arg: (accessToken, user). Preserves pre-A3 behaviour so
    // staff pages don't break.
    if (typeof accessTokenOrResponse === 'string') {
      try { localStorage.setItem(TOKEN_KEY, accessTokenOrResponse); } catch {}
      set({ token: accessTokenOrResponse, user });
      return user;
    }
    // 1-arg: full TokenResponse. Runs the gated router.
    const res = accessTokenOrResponse || {};
    try { localStorage.setItem(TOKEN_KEY, res.access_token); } catch {}
    set({ token: res.access_token, user: res.user });
    routePostAuth(res.user, {
      needs_profile_completion: res.needs_profile_completion,
      must_change_password:     res.must_change_password,
    });
    return res.user;
  },

  /** Refresh `user` from /api/auth/me (used after Complete Profile /
   *  Change Password submits so the gate flags update locally). */
  refreshUser: async () => {
    const t = get().token;
    if (!t) return null;
    try {
      const me = await api.me(t);
      set({ user: me });
      return me;
    } catch {
      return null;
    }
  },

  signOut: () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
    try { localStorage.clear(); } catch {}
    try { sessionStorage.clear(); } catch {}
    set({ token: null, user: null });
    if (_queryClient) {
      try { _queryClient.clear(); } catch {}
    }
  },
}));

// Wire 401 → forced logout (and bounce to /login).
setOn401(() => {
  useAuthStore.getState().signOut();
  if (typeof window !== 'undefined') {
    // Decision §h — host-aware redirect target. Customer host MUST
    // land on `/customer/login`, never the staff `/login` (which is
    // itself blocked on the customer host).
    const target = IS_CUSTOMER_HOST ? '/customer/login' : '/login';
    if (!window.location.pathname.startsWith(target)) {
      window.location.assign(target);
    }
  }
});

// A3 (2026-06-27) — 403 structured-code interceptors. Loyalty pages
// still see the thrown error (so they can show "Rejected" UI) but we
// also redirect behind the scenes.
setOnProfileCompletionRequired(() => {
  if (typeof window === 'undefined') return;
  if (!window.location.pathname.startsWith('/customer/complete-profile')) {
    _goto('/customer/complete-profile');
  }
});
setOnPasswordChangeRequired(() => {
  if (typeof window === 'undefined') return;
  if (!window.location.pathname.startsWith('/customer/change-password')) {
    _goto('/customer/change-password');
  }
});

/**
 * R-2 — login-redirect helper. Preserves the pre-A3 API used by the
 * staff pages and `App.js#RootRedirect`.
 */
export function routeForRole(role) {
  return landingForRole(role);
}
